import { useState } from 'react';
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageSEO } from '@/hooks/usePageSEO';

const contactSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Contact Jeff Dumè',
  url: 'https://jeffdume.com/contact',
  description: 'Contact Jeff Dumè for speaking engagements, consulting inquiries, partnership opportunities, or to learn more about Mainframe Solutions and BuildFit AI.',
  mainEntity: {
    '@type': 'Person',
    name: 'Jeff Dumè',
    email: 'jeff.dume@mainframesolutions.net',
    url: 'https://jeffdume.com',
    sameAs: ['https://www.linkedin.com/in/jdume/', 'https://www.linkedin.com/company/mainframesolutions/', 'https://www.linkedin.com/company/buildfitai/'],
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Houston',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
  },
};

const hubSpotLink = "https://meetings-na2.hubspot.com/mainframesolutions/buildfitaistrategy";

export default function Contact() {
  usePageSEO({
    title: 'Contact Jeff Dumè | Book a Strategy Call | Mainframe Solutions & BuildFit AI',
    description: 'Contact Jeff Dumè for speaking engagements, executive search consulting, AI governance inquiries, or to book a strategy call. Connect for Mainframe Solutions and BuildFit AI partnerships.',
    keywords: 'contact Jeff Dumè, book strategy call, executive search consultation, AI governance inquiry, speaking engagement booking, Mainframe Solutions contact, BuildFit AI demo, Houston workforce consultant',
    canonical: '/contact',
    ogTitle: 'Contact Jeff Dumè | Book a Strategy Call',
    ogDescription: 'Connect for speaking engagements, consulting inquiries, or to book a 30-minute strategy call about executive search, AI governance, and workforce infrastructure.',
    schema: contactSchema,
    breadcrumbs: [{ name: 'Contact', item: '/contact' }],
  });

  const [activeTab, setActiveTab] = useState<'contact' | 'book'>('contact');

  return (
    <div className="bg-charcoal min-h-screen">
      <Navbar />
      <main className="pt-20 sm:pt-24 md:pt-28">
        <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 py-10 sm:py-16 md:py-24 max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Left Column — Intro */}
            <div>
              <p className="text-gold text-xs font-semibold tracking-[0.25em] uppercase mb-3 sm:mb-4">Get in Touch</p>
              <h1 className="text-white text-2xl sm:text-3xl lg:text-display-sm font-bold leading-tight mb-3 sm:mb-4">Let&apos;s Build Something</h1>
              <p className="text-white/50 text-sm md:text-base leading-relaxed mb-6 sm:mb-8">
                Connect for speaking engagements, consulting inquiries, partnership opportunities, or to learn more about Mainframe Solutions and BuildFit AI.
              </p>
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/10 rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-linkedin-fill text-gold text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">BuildFit AI on LinkedIn</p>
                      <p className="text-white/40 text-xs">AI-powered hiring intelligence platform.</p>
                    </div>
                  </div>
                  <a href="https://www.linkedin.com/company/buildfitai/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-gold text-sm font-medium hover:text-gold-light transition-colors whitespace-nowrap">
                    Connect on LinkedIn<i className="ri-arrow-right-line"></i>
                  </a>
                </div>
                <div className="bg-dark-card rounded-xl p-4 sm:p-5 border border-white/5">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gold/10 rounded-full flex items-center justify-center shrink-0">
                      <i className="ri-linkedin-fill text-gold text-lg"></i>
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">Mainframe Solutions on LinkedIn</p>
                      <p className="text-white/40 text-xs">Professional services &amp; workforce infrastructure.</p>
                    </div>
                  </div>
                  <a href="https://www.linkedin.com/company/mainframesolutions/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-3 text-gold text-sm font-medium hover:text-gold-light transition-colors whitespace-nowrap">
                    Connect on LinkedIn<i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column — Tabs */}
            <div>
              <div className="flex p-1 bg-dark-surface rounded-full mb-5 sm:mb-6">
                <button onClick={() => setActiveTab('contact')} className={`flex-1 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 px-3 sm:px-4 rounded-full transition-all whitespace-nowrap cursor-pointer ${activeTab === 'contact' ? 'bg-gold text-charcoal' : 'text-white/60 hover:text-white'}`}>
                  Contact Information
                </button>
                <button onClick={() => setActiveTab('book')} className={`flex-1 text-xs sm:text-sm font-semibold py-2 sm:py-2.5 px-3 sm:px-4 rounded-full transition-all whitespace-nowrap cursor-pointer ${activeTab === 'book' ? 'bg-gold text-charcoal' : 'text-white/60 hover:text-white'}`}>
                  Book a Strategy Call
                </button>
              </div>

              <div className="bg-dark-card rounded-xl p-5 sm:p-6 md:p-8 border border-white/5 min-h-[380px] sm:min-h-[420px]">
                {activeTab === 'contact' && (
                  <div className="space-y-4 sm:space-y-5 animate-[fadeIn_0.3s_ease-out]">
                    <h3 className="text-white font-semibold text-base mb-1">Reach Out Directly</h3>
                    <p className="text-white/40 text-xs mb-3 sm:mb-4">Choose the best way to connect with Jeff Dumè.</p>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-dark-surface rounded-lg shrink-0"><i className="ri-mail-line text-white/50 text-sm"></i></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/30 text-[10px] uppercase tracking-wide mb-1">Email</p>
                        <div className="flex flex-col gap-1.5">
                          <a href="mailto:jeff.dume@mainframesolutions.net" className="text-white text-sm hover:text-gold transition-colors">jeff.dume@mainframesolutions.net</a>
                          <a href="mailto:info@buildfitai.net" className="text-white text-sm hover:text-gold transition-colors">info@buildfitai.net</a>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-dark-surface rounded-lg shrink-0"><i className="ri-global-line text-white/50 text-sm"></i></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/30 text-[10px] uppercase tracking-wide mb-1">Websites</p>
                        <div className="flex flex-col gap-1.5">
                          <a href="https://mainframesolutions.net/" target="_blank" rel="noopener noreferrer" className="text-white text-sm hover:text-gold transition-colors inline-flex items-center gap-1.5">Mainframe Solutions<i className="ri-external-link-line text-xs text-white/40"></i></a>
                          <a href="https://buildfitai.net/" target="_blank" rel="noopener noreferrer" className="text-white text-sm hover:text-gold transition-colors inline-flex items-center gap-1.5">BuildFit AI<i className="ri-external-link-line text-xs text-white/40"></i></a>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-dark-surface rounded-lg shrink-0"><i className="ri-linkedin-fill text-white/50 text-sm"></i></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white/30 text-[10px] uppercase tracking-wide mb-1">LinkedIn</p>
                        <a href="https://www.linkedin.com/in/jdume/" target="_blank" rel="noopener noreferrer" className="text-white text-sm hover:text-gold transition-colors inline-flex items-center gap-1.5">Jeff Dumè<i className="ri-external-link-line text-xs text-white/40"></i></a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-dark-surface rounded-lg shrink-0"><i className="ri-map-pin-line text-white/50 text-sm"></i></div>
                      <div><p className="text-white/30 text-[10px] uppercase tracking-wide">Location</p><p className="text-white text-sm">Texas</p></div>
                    </div>
                  </div>
                )}
                {activeTab === 'book' && (
                  <div className="flex flex-col items-center text-center animate-[fadeIn_0.3s_ease-out]">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gold/10 rounded-full flex items-center justify-center mb-4 sm:mb-5"><i className="ri-calendar-check-line text-gold text-xl sm:text-2xl"></i></div>
                    <h3 className="text-white font-semibold text-base sm:text-lg mb-2">Schedule a Strategy Call</h3>
                    <p className="text-white/50 text-sm leading-relaxed max-w-xs mb-6 sm:mb-8">Book a 30-minute discovery call to discuss hiring strategy, workforce planning, or AI governance for your organization.</p>
                    <a href={hubSpotLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold text-charcoal font-semibold text-sm px-6 sm:px-8 py-3.5 sm:py-4 rounded-md hover:bg-gold-light transition-colors whitespace-nowrap">
                      Book a Strategy Call<i className="ri-arrow-right-line"></i>
                    </a>
                    <p className="text-white/20 text-xs mt-4">Powered by HubSpot Meetings</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}